import { observer } from 'mobx-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { getBirthdayFromSsn } from 'common/src/vtj/ssn'
import {
  Block,
  Checkbox,
  Heading,
  Paragraph,
  Text,
} from 'suomifi-ui-components'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'

import { format } from 'date-fns'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import {
  FormContainer,
  InfoInlineAlert,
  NonEditableField,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { validateSpouseNotRelated } from 'todistukset-common/src/vtj/validation'
import { useAetSignerFormActions } from 'todistukset-ui/src/vtj/stores/actions/aet-signer-form-actions'
import {
  AetOrdererUiForm,
  AetSignerUiForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { useAetSignerForm } from 'todistukset-ui/src/vtj/stores/aet-signer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

const AetSpouseInfoStep: StepComponent = observer(
  forwardRef(function AetSpouseInfoStep({ titleId, orderFlowName }, ref) {
    const { t } = useTranslation()

    const {
      steps: {
        [orderFlowName]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const { setSukulaisuussuhdeVakuutettu } = useAetSignerFormActions()

    const form = useAetSignerForm()
    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getNotRelatedCheckboxErrors,
      ['notRelatedCheckbox']
    )

    if (!form.aetTodistuspyynto) {
      return <></>
    }

    const {
      aetTodistuspyynto: {
        orderer: {
          firstNames: spouseAFirstNames,
          lastName: spouseALastName,
          hetu: spouseAHetu,
        },
      },
      sukulaisuussuhdeVakuutettu,
    } = form

    const spousesCurrentName = `${finalEditableFieldValue(
      spouseALastName
    )}, ${finalEditableFieldValue(spouseAFirstNames)}`
    const spousesBirthdate = format(
      getBirthdayFromSsn(spouseAHetu),
      'dd.MM.yyyy'
    )

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetSignerSpouseInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="aetSignerSpouseInfoMainDescription_paragraphs" />
        </Block>
        <Block variant="section">
          <Columns data-test-id="spouse-info-items">
            <InfoItem
              title={t('aetSignerSpouseInfoSpouseNameLabel')}
              content={spousesCurrentName}
            />
            <InfoItem
              title={t('aetSignerSpouseInfoSpouseBirthdateLabel')}
              content={spousesBirthdate}
            />
          </Columns>
          <Checkbox
            my="xl"
            variant="small"
            checked={!!sukulaisuussuhdeVakuutettu.ilmoitettuArvo}
            onClick={setSukulaisuussuhdeVakuutettu}
            {...errorStatus({
              field: 'notRelatedCheckbox',
              form,
              showErrors,
              t,
            })}
            forwardedRef={refs.notRelatedCheckbox}
          >
            <LocalizedString stringKey="aetSpouseNotRelatedCheckboxLabel" />
          </Checkbox>
          <InfoInlineAlert aria-live="polite" mb="xxl">
            <LocalizedString stringKey="aetSpouseNotRelatedDescription_html" />
          </InfoInlineAlert>
        </Block>
      </FormContainer>
    )
  })
)

const InfoItem: React.FC<{
  title: string
  content: string
}> = ({ title, content }) => (
  <NonEditableField>
    <Paragraph>
      <Text variant="bold">{title}</Text>
    </Paragraph>
    <Paragraph>{content}</Paragraph>
  </NonEditableField>
)

export default AetSpouseInfoStep

type Form = AetOrdererUiForm | AetSignerUiForm

const errorStatus = (params: {
  field: keyof ReturnType<typeof getNotRelatedCheckboxErrors>
  form: Form
  showErrors: boolean
  t: TFunction
}) => {
  return getErrorStatus({
    ...params,
    errors: {
      ...getNotRelatedCheckboxErrors(params.form),
    },
  })
}

const getNotRelatedCheckboxErrors = (form: Form) => {
  return {
    notRelatedCheckbox: validateSpouseNotRelated(
      finalEditableFieldValue(form.sukulaisuussuhdeVakuutettu)
    ),
  }
}
